import { CalendarDays, Users, Stethoscope, UserPlus } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { KPICard } from "../../../components/admin/dashboard/KPICard";
import { DailyTrendChart } from "../../../components/admin/dashboard/charts/DailyTrendChart";
import { SpecialtyChart } from "../../../components/admin/dashboard/charts/SpecialtyChart";
import { AppointmentTable } from "../../../components/admin/dashboard/AppointmentTable";
import { Skeleton } from "../../../components/ui/skeleton";
import { Card } from "../../../components/ui/card";
import type { DashboardStats, DailyTrendData, SpecialtyChartData, Appointment, Department } from "../../../shared/schema";

interface AdminDashboardProps {
  dateRange: "today" | "week";
}

export function AdminDashboard({ dateRange }: AdminDashboardProps) {
  const { data: stats, isLoading: statsLoading } = useQuery<DashboardStats>({
    queryKey: ["/api/stats"],
  });

  const { data: dailyTrend, isLoading: trendLoading } = useQuery<DailyTrendData[]>({
    queryKey: ["/api/charts/daily-trend"],
  });

  const { data: specialtyData, isLoading: specialtyLoading } = useQuery<SpecialtyChartData[]>({
    queryKey: ["/api/charts/specialty"],
  });

  const { data: appointments, isLoading: appointmentsLoading } = useQuery<Appointment[]>({
    queryKey: ["/api/appointments", { range: dateRange }],
  });

  const { data: departments } = useQuery<Department[]>({
    queryKey: ["/api/departments"],
  });

  const isLoading = statsLoading || trendLoading || specialtyLoading || appointmentsLoading;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statsLoading ? (
          <>
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="p-6 border border-card-border">
                <Skeleton className="h-4 w-24 mb-4" />
                <Skeleton className="h-8 w-16 mb-2" />
                <Skeleton className="h-3 w-32" />
              </Card>
            ))}
          </>
        ) : (
          <>
            <KPICard
              title="Lịch hẹn hôm nay"
              value={stats?.todayAppointments || 0}
              subtitle="Tổng số lịch hẹn trong ngày"
              icon={CalendarDays}
              variant="primary"
            />
            <KPICard
              title="Lịch hẹn tuần này"
              value={stats?.weekAppointments || 0}
              subtitle="Tổng số lịch hẹn trong tuần"
              icon={CalendarDays}
              variant="default"
            />
            <KPICard
              title="Bác sĩ có lịch"
              value={stats?.activeDoctors || 0}
              subtitle="Bác sĩ đang làm việc hôm nay"
              icon={Stethoscope}
              variant="success"
            />
            <KPICard
              title="Bệnh nhân mới"
              value={stats?.newPatients || 0}
              subtitle="Bệnh nhân mới trong tuần"
              icon={UserPlus}
              variant="warning"
            />
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {trendLoading ? (
          <Card className="p-6 border border-card-border overflow-hidden">
            <Skeleton className="h-4 w-48 mb-6" />
            <Skeleton className="h-[300px] w-full" />
          </Card>
        ) : (
          <DailyTrendChart data={dailyTrend || []} />
        )}
        
        {specialtyLoading ? (
          <Card className="p-6 border border-card-border overflow-hidden">
            <Skeleton className="h-4 w-48 mb-6" />
            <Skeleton className="h-[300px] w-full" />
          </Card>
        ) : (
          <SpecialtyChart data={specialtyData || []} />
        )}
      </div>

      {appointmentsLoading ? (
        <Card className="p-6 border border-card-border overflow-hidden">
          <Skeleton className="h-4 w-48 mb-6" />
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </Card>
      ) : (
        <AppointmentTable
          appointments={appointments || []}
          departments={departments || []}
          showDepartment={true}
          showDoctor={true}
          title={dateRange === "today" ? "Lịch hẹn hôm nay" : "Lịch hẹn tuần này"}
          emptyMessage="Không có lịch hẹn nào"
        />
      )}
    </div>
  );
}
